import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, TrendingUp, LogOut, Tag, Package, ExternalLink, Settings, LayoutDashboard, MessageSquare, Image as ImageIcon, Menu, X, ChevronLeft, Eye, Sparkles, Grid, Layers, Users, ShoppingCart } from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

import config from "../../config/config";
import { logout } from "../../redux/slices/adminSlice";
import { useDispatch } from "react-redux";

import VisitorsCard from "../../Components/Visitors/VisitorsCard";
import VisitorDetail from "../../Components/Visitors/VisitorDetail";
import DashboardStats from "../../Components/Visitors/DashboardStats";
import HomepageContentEditor from "../../Components/Dashboard/HomepageContentEditor";

const tabsData = [
  { key: "dashboard", label: "Dashboard Home", icon: LayoutDashboard },
  { key: "brands", label: "Brand Statistics", icon: Tag },
  { key: "query-searches", label: "Query Searches", icon: Search },
  { key: "product-clicks", label: "Product Clicks", icon: Package },
  { key: "buy-clicks", label: "Buy Clicks", icon: ShoppingCart },
  { key: "image-searches", label: "Image Searches", icon: ImageIcon },
  { key: "visitors", label: "All Visitors", icon: Users },
  { key: "feedback", label: "Search Feedback", icon: MessageSquare },
  { key: "configure-homepage", label: "Configure Homepage", icon: Settings },
];

const Pagination = ({ pagination, onPageChange }) => {
  const [inputPage, setInputPage] = useState(pagination?.page || pagination?.currentPage || 1);

  useEffect(() => {
    setInputPage(pagination?.page || pagination?.currentPage || 1);
  }, [pagination?.page, pagination?.currentPage]);

  if (!pagination || pagination.totalPages <= 1) return null;
  const page = parseInt(pagination.page || pagination.currentPage, 10);
  const totalPages = parseInt(pagination.totalPages, 10);

  const handleInputSubmit = (e) => {
    if (e.key === "Enter" || e.type === "blur") {
      let newPage = parseInt(inputPage, 10);
      if (isNaN(newPage)) newPage = page;
      if (newPage < 1) newPage = 1;
      if (newPage > totalPages) newPage = totalPages;
      setInputPage(newPage);
      if (newPage !== page) {
        onPageChange(newPage);
      }
    }
  };

  return (
    <div className="flex justify-center items-center gap-4 py-4 border-t">
      <button
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        className="px-4 py-2 border rounded font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 transition-colors"
      >
        Previous
      </button>
      <span className="text-gray-600 font-medium flex items-center gap-2">
        Page
        <input
          type="number"
          value={inputPage}
          onChange={(e) => setInputPage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleInputSubmit(e)}
          onBlur={handleInputSubmit}
          className="border rounded px-2 py-1 w-16 text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
          min={1}
          max={totalPages}
        />
        of {totalPages}
      </span>
      <button
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
        className="px-4 py-2 border rounded font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 transition-colors"
      >
        Next
      </button>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* -------------------- STATE -------------------- */
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [imgDraftStartDate, setImgDraftStartDate] = useState("");
  const [imgDraftEndDate, setImgDraftEndDate] = useState("");
  const [imgAppliedStartDate, setImgAppliedStartDate] = useState("");
  const [imgAppliedEndDate, setImgAppliedEndDate] = useState("");

  const [prodClicks, setProdClicks] = useState([]);
  const [prodClicksPagination, setProdClicksPagination] = useState({ page: 1, totalPages: 1 });
  const [prodClicksSort, setProdClicksSort] = useState("latest");
  const [prodClicksDraftStartDate, setProdClicksDraftStartDate] = useState("");
  const [prodClicksDraftEndDate, setProdClicksDraftEndDate] = useState("");
  const [prodClicksAppliedStartDate, setProdClicksAppliedStartDate] = useState("");
  const [prodClicksAppliedEndDate, setProdClicksAppliedEndDate] = useState("");
  const [prodClicksTotalEvents, setProdClicksTotalEvents] = useState(0);

  const [buyClicks, setBuyClicks] = useState([]);
  const [buyClicksPagination, setBuyClicksPagination] = useState({ page: 1, totalPages: 1 });
  const [buyClicksSort, setBuyClicksSort] = useState("latest");
  const [buyClicksDraftStartDate, setBuyClicksDraftStartDate] = useState("");
  const [buyClicksDraftEndDate, setBuyClicksDraftEndDate] = useState("");
  const [buyClicksAppliedStartDate, setBuyClicksAppliedStartDate] = useState("");
  const [buyClicksAppliedEndDate, setBuyClicksAppliedEndDate] = useState("");
  const [buyClicksTotalEvents, setBuyClicksTotalEvents] = useState(0);


  const [statsDraftStartDate, setStatsDraftStartDate] = useState("");
  const [statsDraftEndDate, setStatsDraftEndDate] = useState("");
  const [statsAppliedStartDate, setStatsAppliedStartDate] = useState("");
  const [statsAppliedEndDate, setStatsAppliedEndDate] = useState("");

  const [visitors, setVisitors] = useState([]);
  const [visitorsSort, setVisitorsSort] = useState("latest");
  const [visitorsPage, setVisitorsPage] = useState(1);
  const [visitorsPagination, setVisitorsPagination] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedVisitorSearchQuery, setAppliedVisitorSearchQuery] = useState("");
  const [loadingVisitorId, setLoadingVisitorId] = useState(null);

  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [selectedVisitorJourney, setSelectedVisitorJourney] = useState(null);

  const [collapsedActions, setCollapsedActions] = useState({});
  const [expandedSessions, setExpandedSessions] = useState({});

  /* -------- Sidebar / Navigation -------- */
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const LIMIT = 20;

  // Query Searches
  const [keywords, setKeywords] = useState([]);
  const [querySearchInput, setQuerySearchInput] = useState("");
  const [querySearchQuery, setQuerySearchQuery] = useState("");
  const [querySort, setQuerySort] = useState("latest");
  const [queryStartDate, setQueryStartDate] = useState("");
  const [queryEndDate, setQueryEndDate] = useState("");
  const [queryPage, setQueryPage] = useState(1);
  const [queryPagination, setQueryPagination] = useState(null);
  const [queryTotalEvents, setQueryTotalEvents] = useState(0);

  // Brands
  const [brands, setBrands] = useState([]);
  const [brandsSearchInput, setBrandsSearchInput] = useState("");
  const [brandsSearchQuery, setBrandsSearchQuery] = useState("");
  const [brandsSort, setBrandsSort] = useState("latest");
  const [brandsStartDate, setBrandsStartDate] = useState("");
  const [brandsEndDate, setBrandsEndDate] = useState("");
  const [brandsPage, setBrandsPage] = useState(1);
  const [brandsPagination, setBrandsPagination] = useState(null);
  const [brandsTotalEvents, setBrandsTotalEvents] = useState(0);
  const [brandsTotalImpressions, setBrandsTotalImpressions] = useState(0);
  const [brandsTotalProductClicks, setBrandsTotalProductClicks] = useState(0);
  const [brandsTotalBuyClicks, setBrandsTotalBuyClicks] = useState(0);

  // Image Searches
  const [imageSearches, setImageSearches] = useState([]);
  const [imageSearchesSort, setImageSearchesSort] = useState("latest");
  const [imageSearchesStartDate, setImageSearchesStartDate] = useState("");
  const [imageSearchesEndDate, setImageSearchesEndDate] = useState("");
  const [imageSearchesPage, setImageSearchesPage] = useState(1);
  const [imageSearchesPagination, setImageSearchesPagination] = useState(null);
  const [imageSearchesTotalEvents, setImageSearchesTotalEvents] = useState(0);

  // Drill-down State for Brands
  const [selectedDetailBrand, setSelectedDetailBrand] = useState(null);
  const [selectedDetailType, setSelectedDetailType] = useState(null); // 'impressions', 'product_clicks', 'buy_clicks'
  const [brandDetailData, setBrandDetailData] = useState([]);
  const [loadingBrandDetails, setLoadingBrandDetails] = useState(false);
  const [brandDetailSort, setBrandDetailSort] = useState("latest");
  const [brandDetailPage, setBrandDetailPage] = useState(1);
  const [brandDetailPagination, setBrandDetailPagination] = useState(null);
  const [brandDetailTotal, setBrandDetailTotal] = useState(0);

  // Feedback
  const [allSearchFeedbacks, setAllSearchFeedbacks] = useState([]);
  const [expandedKeyword, setExpandedKeyword] = useState(null);
  const [keywordVisitors, setKeywordVisitors] = useState([]);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [brandVisitors, setBrandVisitors] = useState([]);
  const [expandedClickItem, setExpandedClickItem] = useState(null);
  const [clickVisitors, setClickVisitors] = useState([]);
  const [loadingListVisitors, setLoadingListVisitors] = useState(false);
  const [feedbackSort, setFeedbackSort] = useState("latest");
  const [feedbackStartDate, setFeedbackStartDate] = useState("");
  const [feedbackEndDate, setFeedbackEndDate] = useState("");
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackPagination, setFeedbackPagination] = useState(null);

  /* -------------------- API CALLS -------------------- */

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${config.backendUrl}/analytics/dashboard/summary`,
        {
          params: {
            startDate: statsAppliedStartDate || undefined,
            endDate: statsAppliedEndDate || undefined,
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSummary(res.data.stats);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/visitors`, {
        params: {
          limit: LIMIT,
          page: visitorsPage,
          sort: visitorsSort,
          searchQuery: appliedVisitorSearchQuery || undefined,
          startDate: appliedStartDate || undefined,
          endDate: appliedEndDate || undefined,
        },
      });
      setVisitors(res.data.visitors);
      setVisitorsPagination(res.data.pagination);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleVisitor = async (visitorId) => {
    setLoading(true);
    setLoadingVisitorId(visitorId);
    try {
      const res = await axios.get(
        `${config.backendUrl}/analytics/visitor/${visitorId}`,
      );
      setSelectedVisitor(visitorId);
      setSelectedVisitorJourney(res.data);
    } finally {
      setLoading(false);
      setLoadingVisitorId(null);
    }
  };

  const fetchKeywords = async () => {
    setTabLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/keywords`, {
        params: {
          limit: LIMIT,
          page: queryPage,
          searchQuery: querySearchQuery,
          sort: querySort,
          startDate: queryStartDate || undefined,
          endDate: queryEndDate || undefined,
        },
      });
      setKeywords(res.data.data || []);
      setQueryPagination(res.data.pagination);
      setQueryTotalEvents(res.data.totalEvents || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchBrands = async () => {
    setTabLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/visited-brands`, {
        params: {
          limit: LIMIT,
          page: brandsPage,
          searchQuery: brandsSearchQuery,
          sort: brandsSort,
          startDate: brandsStartDate || undefined,
          endDate: brandsEndDate || undefined,
        },
      });
      setBrands(res.data.data || []);
      setBrandsPagination(res.data.pagination);
      setBrandsTotalEvents(res.data.totalEvents || 0);
      setBrandsTotalImpressions(res.data.totalImpressions || 0);
      setBrandsTotalProductClicks(res.data.totalProductClicks || 0);
      setBrandsTotalBuyClicks(res.data.totalBuyClicks || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchBrandDetails = async (brand, type, pageToFetch = brandDetailPage) => {
    setSelectedDetailBrand(brand);
    setSelectedDetailType(type);
    setBrandDetailPage(pageToFetch);
    setBrandDetailData([]);
    setLoadingBrandDetails(true);
    try {
      const endpoint = type === 'impressions'
        ? `/analytics/brand-details/${encodeURIComponent(brand)}/impressions`
        : `/analytics/brand-details/${encodeURIComponent(brand)}/clicks`;

      const params = {
        startDate: brandsStartDate || undefined,
        endDate: brandsEndDate || undefined,
        limit: LIMIT,
        page: pageToFetch,
      };

      params.sort = brandDetailSort;
      if (type !== 'impressions') {
        params.type = type;
      }

      const res = await axios.get(`${config.backendUrl}${endpoint}`, { params });
      setBrandDetailData(res.data.details || []);
      setBrandDetailPagination(res.data.pagination || null);
      setBrandDetailTotal(res.data.totalValue || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBrandDetails(false);
    }
  };

  // Re-fetch brand details if sort or page changes
  useEffect(() => {
    if (selectedDetailBrand && selectedDetailType) {
      fetchBrandDetails(selectedDetailBrand, selectedDetailType, brandDetailPage);
    }
  }, [brandDetailSort, brandDetailPage]);

  const fetchGlobalProductClicks = async () => {
    setTabLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/all-product-clicks`, {
        params: {
          limit: LIMIT,
          page: prodClicksPagination.page,
          sort: prodClicksSort,
          startDate: prodClicksAppliedStartDate || undefined,
          endDate: prodClicksAppliedEndDate || undefined,
        },
      });
      setProdClicks(res.data.data || []);
      setProdClicksPagination(res.data.pagination);
      setProdClicksTotalEvents(res.data.totalEvents || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchGlobalBuyClicks = async () => {
    setTabLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/all-buy-clicks`, {
        params: {
          limit: LIMIT,
          page: buyClicksPagination.page,
          sort: buyClicksSort,
          startDate: buyClicksAppliedStartDate || undefined,
          endDate: buyClicksAppliedEndDate || undefined,
        },
      });
      setBuyClicks(res.data.data || []);
      setBuyClicksPagination(res.data.pagination);
      setBuyClicksTotalEvents(res.data.totalEvents || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchImageSearches = async () => {
    setTabLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/image-searches`, {
        params: {
          limit: LIMIT,
          page: imageSearchesPage,
          sort: imageSearchesSort,
          startDate: imageSearchesStartDate || undefined,
          endDate: imageSearchesEndDate || undefined,
        },
      });
      setImageSearches(res.data.data || []);
      setImageSearchesPagination(res.data.pagination);
      setImageSearchesTotalEvents(res.data.totalEvents || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchSearchFeedback = async () => {
    setTabLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/searchfeedback/getAll`, {
        params: {
          limit: LIMIT,
          page: feedbackPage,
          sort: feedbackSort,
          startDate: feedbackStartDate || undefined,
          endDate: feedbackEndDate || undefined,
        }
      });
      setAllSearchFeedbacks(res.data.data || []);
      setFeedbackPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchKeywordVisitors = async (keyword, gender, brandsearch_label) => {
    const uniqueKey = `${keyword}_${gender || 'none'}_${brandsearch_label || 'none'}`;
    if (expandedKeyword === uniqueKey) {
      setExpandedKeyword(null);
      return;
    }
    setExpandedKeyword(uniqueKey);
    setLoadingListVisitors(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/keyword-visitors/${encodeURIComponent(keyword)}`, {
        params: {
          startDate: queryStartDate || undefined,
          endDate: queryEndDate || undefined,
          gender: gender || 'null',
          brandsearch_label: brandsearch_label || 'null',
        }
      });
      setKeywordVisitors(res.data.visitors || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingListVisitors(false);
    }
  };

  const fetchBrandVisitors = async (brand) => {
    if (expandedBrand === brand) {
      setExpandedBrand(null);
      return;
    }
    setExpandedBrand(brand);
    setLoadingListVisitors(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/brand-visitors/${encodeURIComponent(brand)}`, {
        params: {
          startDate: brandsStartDate || undefined,
          endDate: brandsEndDate || undefined,
        }
      });
      setBrandVisitors(res.data.visitors || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingListVisitors(false);
    }
  };

  const fetchClickVisitors = async (groupKey, action) => {
    if (expandedClickItem === groupKey) {
      setExpandedClickItem(null);
      return;
    }
    setExpandedClickItem(groupKey);
    setLoadingListVisitors(true);
    let startDate = action === 'product_click' ? prodClicksAppliedStartDate : buyClicksAppliedStartDate;
    let endDate = action === 'product_click' ? prodClicksAppliedEndDate : buyClicksAppliedEndDate;
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/click-visitors/${encodeURIComponent(groupKey)}`, {
        params: {
          action,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        }
      });
      setClickVisitors(res.data.visitors || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingListVisitors(false);
    }
  };

  /* -------------------- EFFECTS -------------------- */

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchVisitors();
  }, [appliedStartDate, appliedEndDate, visitorsPage, visitorsSort, appliedVisitorSearchQuery]);

  useEffect(() => {
    fetchSummary();
  }, [statsAppliedStartDate, statsAppliedEndDate]);

  useEffect(() => {
    if (activeTab === "query-searches") {
      fetchKeywords();
    }
  }, [activeTab, queryPage, querySearchQuery, querySort, queryStartDate, queryEndDate]);

  useEffect(() => {
    if (activeTab === "brands") {
      fetchBrands();
    }
  }, [activeTab, brandsPage, brandsSearchQuery, brandsSort, brandsStartDate, brandsEndDate]);



  useEffect(() => {
    if (activeTab === "image-searches") {
      fetchImageSearches();
    }
  }, [activeTab, imageSearchesPage, imageSearchesSort, imageSearchesStartDate, imageSearchesEndDate]);

  useEffect(() => {
    if (activeTab === "feedback") {
      fetchSearchFeedback();
    }
  }, [activeTab, feedbackPage, feedbackSort, feedbackStartDate, feedbackEndDate]);

  useEffect(() => {
    if (activeTab === "product-clicks") {
      fetchGlobalProductClicks();
    }
  }, [activeTab, prodClicksPagination.page, prodClicksSort, prodClicksAppliedStartDate, prodClicksAppliedEndDate]);

  useEffect(() => {
    if (activeTab === "buy-clicks") {
      fetchGlobalBuyClicks();
    }
  }, [activeTab, buyClicksPagination.page, buyClicksSort, buyClicksAppliedStartDate, buyClicksAppliedEndDate]);

  /* -------------------- HELPERS -------------------- */

  const isInvalidDateRange = () => {
    if (!draftStartDate && !draftEndDate) return false;
    if (!draftStartDate || !draftEndDate) return true;
    return (
      new Date(draftStartDate) > new Date(draftEndDate) ||
      new Date(draftEndDate) > new Date()
    );
  };

  const toggleSession = (id) =>
    setExpandedSessions((p) => ({ ...p, [id]: !p[id] }));

  const toggleActionCollapse = (key) =>
    setCollapsedActions((p) => ({ ...p, [key]: !p[key] }));

  const getActionKey = (sid, idx) => `${sid}-${idx}`;
  const isCollapsibleAction = () => false;

  const getActionIcon = (a) => {
    if (a === "search") return <Search className="w-5 h-5" />;
    if (a === "category_click") return <Tag className="w-5 h-5" />;
    if (a === "product_click") return <Package className="w-5 h-5" />;
    if (a === "checkout_redirect") return <ExternalLink className="w-5 h-5" />;
    if (a === "new_arrivals_visit") return <TrendingUp className="w-5 h-5 text-indigo-500" />;
    if (a === "on_sale_visit") return <Tag className="w-5 h-5 text-rose-500" />;
    if (a === "view_all_visit") return <Package className="w-5 h-5 text-teal-500" />;
    if (a === "image_search") return <Search className="w-5 h-5 text-fuchsia-500" />;
    if (a === "image_search_buy_click") return <Package className="w-5 h-5 text-pink-500" />;
    return <TrendingUp className="w-5 h-5" />;
  };

  const getActionColor = (a) => {
    if (a === "search") return "bg-blue-50 border-blue-200 text-blue-700";
    if (a === "category_click") return "bg-green-50 border-green-200 text-green-700";
    if (a === "product_click") return "bg-purple-50 border-purple-200 text-purple-700";
    if (a === "checkout_redirect") return "bg-yellow-50 border-yellow-200 text-yellow-700";
    if (a === "new_arrivals_visit") return "bg-indigo-50 border-indigo-200 text-indigo-700";
    if (a === "on_sale_visit") return "bg-rose-50 border-rose-200 text-rose-700";
    if (a === "view_all_visit") return "bg-teal-50 border-teal-200 text-teal-700";
    if (a === "image_search") return "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700";
    if (a === "image_search_product_click") return "bg-pink-50 border-pink-200 text-pink-700";
    return "bg-gray-50 border-gray-200 text-gray-700";
  };

  const getActionBadgeColor = (a) => {
    if (a === "search") return "bg-blue-100 text-blue-800";
    if (a === "category_click") return "bg-green-100 text-green-800";
    if (a === "product_click") return "bg-purple-100 text-purple-800";
    if (a === "checkout_redirect") return "bg-yellow-100 text-yellow-800";
    if (a === "new_arrivals_visit") return "bg-indigo-100 text-indigo-800";
    if (a === "on_sale_visit") return "bg-rose-100 text-rose-800";
    if (a === "view_all_visit") return "bg-teal-100 text-teal-800";
    if (a === "image_search") return "bg-fuchsia-100 text-fuchsia-800";
    if (a === "image_search_buy_click") return "bg-pink-100 text-pink-800";
    return "bg-gray-100 text-gray-800";
  };

  const getActionLabel = (a) => {
    if (a === "search") return "Search";
    if (a === "category_click") return "Category Click";
    if (a === "product_click") return "Product Click";
    if (a === "checkout_redirect") return "Buy Click";
    if (a === "new_arrivals_visit") return "New Arrivals Visit";
    if (a === "on_sale_visit") return "On Sale Visit";
    if (a === "view_all_visit") return "View All Visit";
    if (a === "image_search") return "Image Search";
    if (a === "image_search_buy_click") return "Image Search Buy Click";
    return a;
  };

  const formatTimestamp = (t) => new Date(t).toLocaleString("en-US");
  const formatDate = (t) => new Date(t).toLocaleDateString("en-US");

  /* -------------------- INITIAL LOADING -------------------- */

  if (!summary) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  /* -------------------- SINGLE VISITOR -------------------- */

  if (selectedVisitor) {
    return (
      <>
        <Backdrop open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <VisitorDetail
          visitorId={selectedVisitor}
          staticId={selectedVisitorJourney?.static_id}
          journey={selectedVisitorJourney?.journey || []}
          topBrands={selectedVisitorJourney?.topBrands || []}
          collapsedActions={collapsedActions}
          expandedSessions={expandedSessions}
          toggleSession={toggleSession}
          toggleActionCollapse={toggleActionCollapse}
          getActionKey={getActionKey}
          isCollapsibleAction={isCollapsibleAction}
          getActionIcon={getActionIcon}
          getActionColor={getActionColor}
          getActionBadgeColor={getActionBadgeColor}
          getActionLabel={getActionLabel}
          formatTimestamp={formatTimestamp}
          formatDate={formatDate}
          onBack={() => {
            setSelectedVisitor(null);
            setSelectedVisitorJourney(null);
          }}
        />
      </>
    );
  }

  /* -------------------- DASHBOARD -------------------- */

  return (
    <>
      <Backdrop open={loading} className="z-50 text-white">
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="flex h-screen bg-gray-50 overflow-hidden">
        {/* Hamburger Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`fixed inset-y-0 left-0 w-64 bg-white border-r z-50 transform transition-transform duration-200 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col`}>
          <div className="p-6 border-b flex justify-between items-center">
            <img
              src={require("../../assets/images/handpik.png")}
              alt="Handpik Logo"
              width={162}
              height={32}
              className="cursor-pointer object-contain"
              onClick={() => navigate("/")}
            />
            <button className="p-1 rounded-md hover:bg-gray-100" onClick={() => setIsSidebarOpen(false)}>
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-4">
            <nav className="space-y-1 px-3">
              {tabsData.map((t) => {
                const Icon = t.icon;
                const isActive = activeTab === t.key || (t.subTabs && t.subTabs.some(st => st.key === activeTab));

                return (
                  <div key={t.key} className="space-y-1">
                    <button
                      onClick={() => {
                        setActiveTab(t.key);
                        setSelectedDetailBrand(null); // Reset detail view when clicking sidebar
                        if (!t.subTabs) setIsSidebarOpen(false); // Only close if it's not a parent with sub-tabs, or keep it simple and close on mobile when they select a main tab
                      }}
                      className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                    >
                      <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-indigo-700' : 'text-gray-400'}`} />
                      {t.label}
                    </button>

                    {t.subTabs && (
                      <div className="pl-10 space-y-1 mt-1">
                        {t.subTabs.map(subTab => {
                          const isSubActive = activeTab === subTab.key;
                          return (
                            <button
                              key={subTab.key}
                              onClick={() => {
                                setActiveTab(subTab.key);
                                setSelectedDetailBrand(null); // Reset detail view when clicking sidebar
                                setIsSidebarOpen(false);
                              }}
                              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isSubActive
                                ? "text-indigo-700 bg-indigo-50/50"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full mr-2 ${isSubActive ? 'bg-indigo-600' : 'bg-gray-400'}`} />
                              {subTab.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          <div className="p-4 border-t">
            <button
              onClick={() => {
                dispatch(logout());
                localStorage.clear();
                navigate("/login");
              }}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Header Title */}
          <div className="bg-white shadow-sm border-b px-4 py-3 flex items-center z-10">
            <button className="p-2 -ml-2 mr-2 text-gray-600 hover:bg-gray-100 rounded-md" onClick={() => setIsSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold">Analytics Dashboard</h1>
          </div>

          <main className={`flex-1 overflow-y-auto ${activeTab === "configure-homepage" ? "p-0" : activeTab === "dashboard" ? "px-4 sm:px-6 lg:px-8 pt-4 pb-2" : "p-4 sm:p-6 lg:p-8"}`}>
            <div className={`max-w-7xl mx-auto ${activeTab === "configure-homepage" ? "" : "space-y-6"}`}>

              {/* BACK BUTTON for sub pages */}
              {activeTab !== "dashboard" && activeTab !== "configure-homepage" && (
                <div className="flex flex-col items-start gap-2 mb-6">
                  <button
                    onClick={() => setActiveTab("dashboard")}
                    className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 mr-1" />
                    Back to Dashboard
                  </button>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {tabsData.find(t => t.key === activeTab)?.label}
                  </h2>
                </div>
              )}

              {/* DASHBOARD HOME */}
              {activeTab === "dashboard" && (
                <>
                  <DashboardStats summary={summary} onNavigate={setActiveTab} />

                  {/* Dashboard Stats Date Picker */}
                  <div className="flex justify-center pb-4 relative z-20">
                    <div className="bg-white p-2.5 rounded-2xl sm:rounded-full shadow-lg shadow-indigo-500/10 border-2 border-indigo-50 flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto mx-4 sm:mx-0">
                      <div className="flex items-center gap-3 w-full sm:w-auto px-2 sm:pl-3">
                        <input
                          type="date"
                          className="flex-1 sm:flex-none bg-indigo-50/50 border border-indigo-100 text-indigo-900 font-medium p-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none focus:bg-white transition-all cursor-pointer"
                          value={statsDraftStartDate}
                          onChange={(e) => setStatsDraftStartDate(e.target.value)}
                        />
                        <span className="text-indigo-300 font-bold">to</span>
                        <input
                          type="date"
                          className="flex-1 sm:flex-none bg-indigo-50/50 border border-indigo-100 text-indigo-900 font-medium p-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none focus:bg-white transition-all cursor-pointer"
                          value={statsDraftEndDate}
                          onChange={(e) => setStatsDraftEndDate(e.target.value)}
                        />
                      </div>
                      <div className="flex items-center gap-2 sm:border-l sm:pl-4 border-indigo-100 pr-1 w-full sm:w-auto justify-end mt-2 sm:mt-0">
                        <button
                          onClick={() => {
                            setStatsAppliedStartDate(statsDraftStartDate);
                            setStatsAppliedEndDate(statsDraftEndDate);
                          }}
                          className="flex-1 sm:flex-none px-6 py-2 bg-indigo-600 text-white font-bold rounded-xl sm:rounded-full text-sm hover:bg-indigo-700 shadow-md shadow-indigo-500/20 transition-all hover:-translate-y-0.5"
                        >
                          Apply
                        </button>
                        <button
                          onClick={() => {
                            setStatsDraftStartDate("");
                            setStatsDraftEndDate("");
                            setStatsAppliedStartDate("");
                            setStatsAppliedEndDate("");
                          }}
                          className="flex-1 sm:flex-none px-4 py-2 text-indigo-600 font-bold rounded-xl sm:rounded-full text-sm hover:bg-indigo-50 transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ALL VISITORS */}
              {activeTab === "visitors" && (
                <div className="relative min-h-[300px]">
                  {tabLoading && (
                    <div className="absolute inset-0 bg-white/50 flex justify-center items-center z-10 rounded-xl">
                      <CircularProgress />
                    </div>
                  )}
                  <VisitorsCard
                    visitors={visitors}
                    sort={visitorsSort}
                    setSort={setVisitorsSort}
                    setPage={setVisitorsPage}
                    pagination={visitorsPagination}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onSearch={() => {
                      setVisitorsPage(1);
                      setAppliedVisitorSearchQuery(searchQuery);
                    }}
                    loadingVisitorId={loadingVisitorId}
                    formatDate={formatDate}
                    draftStartDate={draftStartDate}
                    draftEndDate={draftEndDate}
                    setDraftStartDate={setDraftStartDate}
                    setDraftEndDate={setDraftEndDate}
                    isInvalidDateRange={isInvalidDateRange}
                    onApply={() => {
                      if (!isInvalidDateRange()) {
                        setVisitorsPage(1);
                        setAppliedStartDate(draftStartDate);
                        setAppliedEndDate(draftEndDate);
                      }
                    }}
                    onReset={() => {
                      setVisitorsPage(1);
                      setDraftStartDate("");
                      setDraftEndDate("");
                      setAppliedStartDate("");
                      setAppliedEndDate("");
                    }}
                    onVisitorSelect={fetchSingleVisitor}
                  />
                </div>
              )}

              {/* CONFIGURE HOMEPAGE */}
              {activeTab === "configure-homepage" && (
                <HomepageContentEditor onBack={() => setActiveTab("dashboard")} />
              )}

              {/* OTHER TABS (Query Searches, Brands, etc.) */}
              {["query-searches", "brands", "image-searches", "feedback", "product-clicks", "buy-clicks"].includes(activeTab) && (
                <div className="bg-white rounded-xl shadow mb-8">
                  <div className="relative min-h-[300px]">
                    {tabLoading && (
                      <div className="absolute inset-0 bg-white/50 flex justify-center items-center z-10">
                        <CircularProgress />
                      </div>
                    )}

                    {/* Query Searches Tab Content */}
                    {activeTab === "query-searches" && (
                      <>
                        <div className="p-4 border-b flex flex-col gap-4">
                          <div className="flex gap-2">
                            <input
                              className="flex-1 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="Search query..."
                              value={querySearchInput}
                              onChange={(e) => setQuerySearchInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  setQueryPage(1);
                                  setQuerySearchQuery(querySearchInput);
                                }
                              }}
                            />
                            <button
                              className="bg-indigo-600 text-white px-4 py-2 rounded font-medium hover:bg-indigo-700 transition-colors"
                              onClick={() => {
                                setQueryPage(1);
                                setQuerySearchQuery(querySearchInput);
                              }}
                            >
                              Search
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-3 rounded-lg border">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-600">From:</span>
                              <input
                                type="date"
                                className="border p-1.5 rounded text-sm"
                                value={queryStartDate}
                                onChange={(e) => { setQueryPage(1); setQueryStartDate(e.target.value); }}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-600">To:</span>
                              <input
                                type="date"
                                className="border p-1.5 rounded text-sm"
                                value={queryEndDate}
                                onChange={(e) => { setQueryPage(1); setQueryEndDate(e.target.value); }}
                              />
                            </div>
                            <div className="flex items-center gap-2 ml-auto">
                              <span className="text-sm font-medium text-gray-600">Sort by:</span>
                              <select
                                className="border p-1.5 rounded text-sm"
                                value={querySort}
                                onChange={(e) => { setQueryPage(1); setQuerySort(e.target.value); }}
                              >
                                <option value="latest">Latest</option>
                                <option value="oldest">Oldest</option>
                                <option value="most">Most Frequent</option>
                                <option value="least">Least Frequent</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                          <span className="text-sm font-semibold text-gray-700">Total Searches: {queryTotalEvents.toLocaleString()}</span>
                        </div>

                        <div className="w-full">
                          {keywords.length > 0 ? keywords.map((i) => (
                            <div key={`${i.keyword}_${i.gender || 'none'}_${i.brandsearch_label || 'none'}`} className="border-b last:border-b-0">
                              <div
                                className="p-4 flex justify-between items-center hover:bg-gray-50 cursor-pointer"
                                onClick={() => fetchKeywordVisitors(i.keyword, i.gender, i.brandsearch_label)}
                              >
                                <div className="flex items-center gap-2">
                                  <b>{i.keyword}</b>
                                  {i.gender && (
                                    <span className="text-gray-500 text-sm">({i.gender})</span>
                                  )}
                                  {i.brandsearch_label && (
                                    <span className="text-gray-500 text-sm">({i.brandsearch_label})</span>
                                  )}
                                </div>
                                <span className="text-sm font-medium bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                                  {i.count} {i.count === 1 ? 'search' : 'searches'}
                                </span>
                              </div>
                              {expandedKeyword === `${i.keyword}_${i.gender || 'none'}_${i.brandsearch_label || 'none'}` && (
                                <div className="bg-gray-50 p-4 pt-0 text-sm">
                                  {loadingListVisitors ? (
                                    <div className="text-indigo-600 animate-pulse">Loading visitors...</div>
                                  ) : keywordVisitors.length > 0 ? (
                                    <>
                                      <div className="text-gray-500 font-medium mb-1 mt-2">{keywordVisitors.length} Unique Visitors</div>
                                      <div className="flex flex-wrap gap-2">
                                        {keywordVisitors.map(v => (
                                          <span
                                            key={v.visitor_id}
                                            onClick={() => { setSelectedVisitor(v.visitor_id); fetchSingleVisitor(v.visitor_id); }}
                                            className="bg-white border px-2 py-1 rounded shadow-sm text-indigo-600 cursor-pointer hover:bg-indigo-50"
                                          >
                                            ID: {v.static_id || "-"}
                                          </span>
                                        ))}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-gray-500 mt-2">No visitors found.</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )) : (
                            <div className="text-center py-8 text-gray-500">No searches found.</div>
                          )}
                        </div>
                        <Pagination pagination={queryPagination} onPageChange={setQueryPage} />
                      </>
                    )}

                    {/* Brands & Sub-Tabs Content */}
                    {activeTab === "brands" && (
                      <>
                        {selectedDetailBrand ? (
                          // DRILL DOWN VIEW
                          <div className="flex flex-col">
                            <div className="p-4 border-b flex items-center gap-4 bg-gray-50">
                              <button
                                onClick={() => {
                                  setSelectedDetailBrand(null);
                                  setSelectedDetailType(null);
                                  setBrandDetailPage(1);
                                }}
                                className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                              >
                                <ChevronLeft className="w-5 h-5 mr-1" />
                                Back
                              </button>
                              <h2 className="text-xl font-bold text-gray-900 border-l pl-4 border-gray-300 flex items-center gap-3">
                                {selectedDetailBrand} - {
                                  selectedDetailType === 'impressions' ? 'Impressions' :
                                    selectedDetailType === 'product_clicks' ? 'Product Clicks' :
                                      selectedDetailType === 'buy_clicks' ? 'Buy Clicks' : 'Conversions'
                                }
                              </h2>
                            </div>

                            {/* Sort Controls for Detail View */}
                            <div className="flex justify-end p-4 border-b">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-600">Sort by:</span>
                                <select
                                  className="border p-1.5 rounded text-sm"
                                  value={brandDetailSort}
                                  onChange={(e) => { setBrandDetailPage(1); setBrandDetailSort(e.target.value); }}
                                >
                                  <option value="latest">
                                    {selectedDetailType === 'impressions' ? 'Latest Impression' : 'Latest Clicked'}
                                  </option>
                                  <option value={selectedDetailType === 'impressions' ? 'most_impressions' : 'most_clicks'}>
                                    {selectedDetailType === 'impressions' ? 'Most Impressions' : selectedDetailType === 'conversions' ? 'Most Conversions' : 'Most Clicks'}
                                  </option>
                                </select>
                              </div>
                            </div>

                            <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                              <span className="text-sm font-semibold text-gray-700">
                                {selectedDetailType === 'impressions' && `Total Impressions: ${brandDetailTotal.toLocaleString()}`}
                                {selectedDetailType === 'product_clicks' && `Total Product Clicks: ${brandDetailTotal.toLocaleString()}`}
                                {selectedDetailType === 'buy_clicks' && `Total Buy Clicks: ${brandDetailTotal.toLocaleString()}`}
                                {selectedDetailType === 'conversions' && `Total Conversions: ${brandDetailTotal.toLocaleString()}`}
                              </span>
                            </div>

                            <div className="w-full">
                              {loadingBrandDetails ? (
                                <div className="p-8 flex justify-center text-indigo-600 animate-pulse">Loading details...</div>
                              ) : brandDetailData.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">No data found for this brand.</div>
                              ) : selectedDetailType === "impressions" ? (
                                /* IMPRESSIONS LIST */
                                <div className="flex flex-col">
                                  <div className="flex justify-between px-6 py-2 bg-gray-100 border-b text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    <span>Source</span>
                                    <span>Impressions</span>
                                  </div>
                                  {brandDetailData.map((item, idx) => (
                                    <div key={idx} className="p-4 border-b flex flex-col md:flex-row md:items-center items-start gap-4 hover:bg-gray-50">
                                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        {item.source === 'text_search' ? <Search className="w-6 h-6 text-purple-600" /> :
                                          item.source === 'image_search_similar' ? <ImageIcon className="w-6 h-6 text-purple-600" /> :
                                            item.source === 'new_arrivals_page' ? <Sparkles className="w-6 h-6 text-purple-600" /> :
                                              item.source === 'on_sale_page' ? <Tag className="w-6 h-6 text-purple-600" /> :
                                                item.source === 'view_all_page' ? <Grid className="w-6 h-6 text-purple-600" /> :
                                                  item.source === 'similar_products' ? <Layers className="w-6 h-6 text-purple-600" /> :
                                                    <Package className="w-6 h-6 text-purple-600" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold text-gray-900 mb-1 capitalize flex items-center gap-1">
                                          {item.source?.replace(/_/g, ' ') || 'Unknown Source'}
                                        </div>
                                        {/* Only show query and category for text search (source === 'text_search') */}
                                        {item.source === 'text_search' && (item.query || item.category) && (
                                          <div className="flex flex-wrap gap-2 text-sm mt-2">
                                            {item.query && (
                                              <span className="bg-white text-gray-900 px-3 py-1.5 rounded border shadow-sm flex items-center gap-1">
                                                <span className="text-gray-500 font-normal">Query:</span> <span className="font-semibold">"{item.query}" {item.gender && `(${item.gender})`}</span>
                                              </span>
                                            )}
                                            {item.category && (
                                              <span className="bg-white text-gray-900 px-3 py-1.5 rounded border shadow-sm flex items-center gap-1">
                                                <span className="text-gray-500 font-normal">Category Tab:</span> <span className="font-semibold">{item.category}</span>
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-end gap-1 mt-2 md:mt-0">
                                        <span className="text-sm font-medium bg-purple-100 text-purple-800 px-3 py-1 rounded-full whitespace-nowrap">
                                          {item.count} {item.count === 1 ? 'Impression' : 'Impressions'}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                /* CLICKS LIST */
                                <div className="flex flex-col">
                                  {brandDetailData.map((item, idx) => (
                                    <div key={item.product_id || idx} className="p-4 border-b flex items-start gap-4 hover:bg-gray-50">
                                      <div className="w-16 h-16 bg-gray-100 rounded border overflow-hidden flex-shrink-0">
                                        {item.product_image ? (
                                          <img src={item.product_image} alt="Product" className="w-full h-full object-cover" />
                                        ) : (
                                          <Package className="w-8 h-8 m-4 text-gray-400" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-gray-900 truncate mb-1">
                                          {item.product_title || 'Unknown Product'}
                                        </div>
                                        <div className="text-xs text-gray-500 mb-2">ID: {item.product_id}</div>
                                        <div className="text-xs text-gray-400">
                                          Last clicked: {item.latest_click ? formatTimestamp(item.latest_click) : '-'}
                                        </div>
                                      </div>
                                      <div className="flex flex-col items-end gap-1">
                                        {(selectedDetailType === "product_clicks" || selectedDetailType === "conversions") && (
                                          <span className="text-sm font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                            {item.product_clicks} Product Clicks
                                          </span>
                                        )}
                                        {(selectedDetailType === "buy_clicks" || selectedDetailType === "conversions") && (
                                          <span className="text-sm font-medium bg-green-100 text-green-800 px-2 py-1 rounded">
                                            {item.buy_clicks} Buy Clicks
                                          </span>
                                        )}
                                        {selectedDetailType === "conversions" && (
                                          <span className="text-sm font-medium bg-gray-200 text-gray-800 px-2 py-1 rounded">
                                            {item.product_clicks > 0 ? ((item.buy_clicks / item.product_clicks) * 100).toFixed(1) : 0}% Conversion
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            {brandDetailPagination && brandDetailPagination.totalPages > 1 && (
                              <Pagination pagination={brandDetailPagination} onPageChange={setBrandDetailPage} />
                            )}
                          </div>
                        ) : (
                          // LIST VIEW (Brands, Impressions, Product Clicks, Buy Clicks)
                          <>
                            <div className="p-4 border-b flex flex-col gap-4">
                              <div className="flex gap-2">
                                <input
                                  className="flex-1 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  placeholder="Search brands..."
                                  value={brandsSearchInput}
                                  onChange={(e) => setBrandsSearchInput(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      setBrandsPage(1);
                                      setBrandsSearchQuery(brandsSearchInput);
                                    }
                                  }}
                                />
                                <button
                                  className="bg-indigo-600 text-white px-4 py-2 rounded font-medium hover:bg-indigo-700 transition-colors"
                                  onClick={() => {
                                    setBrandsPage(1);
                                    setBrandsSearchQuery(brandsSearchInput);
                                  }}
                                >
                                  Search
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-3 rounded-lg border">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-600">From:</span>
                                  <input
                                    type="date"
                                    className="border p-1.5 rounded text-sm"
                                    value={brandsStartDate}
                                    onChange={(e) => { setBrandsPage(1); setBrandsStartDate(e.target.value); }}
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-600">To:</span>
                                  <input
                                    type="date"
                                    className="border p-1.5 rounded text-sm"
                                    value={brandsEndDate}
                                    onChange={(e) => { setBrandsPage(1); setBrandsEndDate(e.target.value); }}
                                  />
                                </div>
                                <div className="flex items-center gap-2 ml-auto">
                                  <span className="text-sm font-medium text-gray-600">Sort by:</span>
                                  <select
                                    className="border p-1.5 rounded text-sm"
                                    value={brandsSort}
                                    onChange={(e) => { setBrandsPage(1); setBrandsSort(e.target.value); }}
                                  >
                                    <option value="latest">Latest Clicked</option>
                                    <option value="most_impressions">Most Impressions</option>
                                    <option value="most_product">Most Product Clicks</option>
                                    <option value="most_buy">Most Buy Clicks</option>
                                    <option value="most_conversions">Most Conversions</option>
                                  </select>
                                </div>
                              </div>
                            </div>

                            <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                              <span className="text-sm font-semibold text-gray-700">
                                {`Total Impressions: ${brandsTotalImpressions.toLocaleString()} | Total Product Clicks: ${brandsTotalProductClicks.toLocaleString()} | Total Buy Clicks: ${brandsTotalBuyClicks.toLocaleString()}`}
                              </span>
                            </div>

                            <div className="w-full">
                              {brands.length > 0 ? brands.map((i) => (
                                <div key={i.brand} className="border-b last:border-b-0">
                                  <div
                                    className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-2 hover:bg-gray-50 cursor-pointer"
                                    onClick={() => fetchBrandVisitors(i.brand)}
                                  >
                                    <b>{i.brand}</b>
                                    <div className="flex flex-wrap gap-2 justify-end md:justify-start">
                                      <span
                                        className="text-sm font-medium bg-purple-100 text-purple-800 px-2 py-1 rounded hover:bg-purple-200 transition-colors cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          fetchBrandDetails(i.brand, 'impressions', 1);
                                        }}
                                      >
                                        {i.impressions || 0} {(i.impressions || 0) === 1 ? 'impression' : 'impressions'}
                                      </span>

                                      <span
                                        className="text-sm font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200 transition-colors cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          fetchBrandDetails(i.brand, 'product_clicks', 1);
                                        }}
                                      >
                                        {i.product_clicks} {i.product_clicks === 1 ? 'product click' : 'product clicks'}
                                      </span>

                                      <span
                                        className="text-sm font-medium bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200 transition-colors cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          fetchBrandDetails(i.brand, 'buy_clicks', 1);
                                        }}
                                      >
                                        {i.buy_clicks} {i.buy_clicks === 1 ? 'buy click' : 'buy clicks'}
                                      </span>

                                      <span
                                        className="text-sm font-medium bg-gray-200 text-gray-800 px-2 py-1 rounded whitespace-nowrap hover:bg-gray-300 transition-colors cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          fetchBrandDetails(i.brand, 'conversions', 1);
                                        }}
                                      >
                                        {i.product_clicks > 0 ? ((i.buy_clicks / i.product_clicks) * 100).toFixed(1) : 0}% Conversion
                                      </span>
                                    </div>
                                  </div>
                                  {expandedBrand === i.brand && (
                                    <div className="bg-gray-50 p-4 pt-0 text-sm">
                                      {loadingListVisitors ? (
                                        <div className="text-green-600 animate-pulse">Loading visitors...</div>
                                      ) : brandVisitors.length > 0 ? (
                                        <>
                                          <div className="text-gray-500 font-medium mb-1 mt-2">{brandVisitors.length} Unique Visitors</div>
                                          <div className="flex flex-wrap gap-2">
                                            {brandVisitors.map(v => (
                                              <span
                                                key={v.visitor_id}
                                                onClick={() => { setSelectedVisitor(v.visitor_id); fetchSingleVisitor(v.visitor_id); }}
                                                className="bg-white border px-2 py-1 rounded shadow-sm text-green-600 cursor-pointer hover:bg-green-50"
                                              >
                                                ID: {v.static_id || "-"}
                                              </span>
                                            ))}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="text-gray-500 mt-2">No visitors found.</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )) : (
                                <div className="text-center py-8 text-gray-500">No brands found.</div>
                              )}
                            </div>
                            <Pagination pagination={brandsPagination} onPageChange={setBrandsPage} />
                          </>
                        )}
                      </>
                    )}

                    {/* Product Clicks Tab Content */}
                    {activeTab === "product-clicks" && (
                      <>
                        <div className="p-4 border-b flex flex-wrap gap-4 items-center bg-gray-50 rounded-t-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">From:</span>
                            <input
                              type="date"
                              className="border p-1.5 rounded text-sm"
                              value={prodClicksDraftStartDate}
                              onChange={(e) => setProdClicksDraftStartDate(e.target.value)}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">To:</span>
                            <input
                              type="date"
                              className="border p-1.5 rounded text-sm"
                              value={prodClicksDraftEndDate}
                              onChange={(e) => setProdClicksDraftEndDate(e.target.value)}
                            />
                          </div>
                          <button
                            className="bg-indigo-600 text-white px-4 py-1.5 rounded font-medium hover:bg-indigo-700 transition-colors text-sm"
                            onClick={() => {
                              setProdClicksPagination(p => ({ ...p, page: 1 }));
                              setProdClicksAppliedStartDate(prodClicksDraftStartDate);
                              setProdClicksAppliedEndDate(prodClicksDraftEndDate);
                            }}
                          >
                            Apply
                          </button>
                          <div className="flex items-center gap-2 ml-auto">
                            <span className="text-sm font-medium text-gray-600">Sort by:</span>
                            <select
                              className="border p-1.5 rounded text-sm"
                              value={prodClicksSort}
                              onChange={(e) => { setProdClicksPagination(p => ({ ...p, page: 1 })); setProdClicksSort(e.target.value); }}
                            >
                              <option value="latest">Latest</option>
                              <option value="most">Most Clicks</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                          <span className="text-sm font-semibold text-gray-700">Total Product Clicks: {prodClicksTotalEvents.toLocaleString()}</span>
                        </div>

                        <div className="w-full flex flex-col">
                          {prodClicks.length > 0 ? prodClicks.map((item, idx) => (
                            <div key={item.group_key || idx} className="border-b last:border-b-0">
                              <div
                                className="p-4 flex flex-col md:flex-row items-start md:items-center gap-4 hover:bg-gray-50 cursor-pointer"
                                onClick={() => fetchClickVisitors(item.group_key, 'product_click')}
                              >
                                <div className="w-16 h-16 bg-gray-100 rounded border overflow-hidden flex-shrink-0">
                                  {item.product_image ? (
                                    <img src={item.product_image} alt="Product" className="w-full h-full object-cover" />
                                  ) : (
                                    <Package className="w-8 h-8 m-4 text-gray-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900 truncate mb-1">
                                    {item.brand ? <span className="mr-1">{item.brand} -</span> : null}
                                    {item.product_title || 'Unknown Product'}
                                  </div>
                                  <div className="text-xs text-gray-500 mb-2">ID: {item.product_id}</div>
                                  <div className="text-xs text-gray-400">
                                    Last clicked: {item.latest_click ? formatTimestamp(item.latest_click) : '-'}
                                  </div>
                                  {item.product_url && (
                                    <a
                                      href={item.product_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-block mt-2 text-xs bg-white text-purple-600 border border-purple-200 px-3 py-1.5 rounded shadow-sm font-medium hover:bg-purple-50 transition-colors whitespace-nowrap"
                                    >
                                      View Product
                                    </a>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1 mt-2 md:mt-0">
                                  <span className="text-sm font-medium bg-purple-100 text-purple-800 px-3 py-1 rounded-full whitespace-nowrap">
                                    {item.product_clicks} {item.product_clicks === 1 ? 'Product Click' : 'Product Clicks'}
                                  </span>
                                </div>
                              </div>
                              {expandedClickItem === item.group_key && (
                                <div className="bg-gray-50 p-4 pt-0 text-sm">
                                  {loadingListVisitors ? (
                                    <div className="text-purple-600 animate-pulse">Loading visitors...</div>
                                  ) : clickVisitors.length > 0 ? (
                                    <>
                                      <div className="text-gray-500 font-medium mb-1 mt-2">{clickVisitors.length} Unique Visitors</div>
                                      <div className="flex flex-wrap gap-2">
                                        {clickVisitors.map(v => (
                                          <span
                                            key={v.visitor_id}
                                            onClick={() => { setSelectedVisitor(v.visitor_id); fetchSingleVisitor(v.visitor_id); }}
                                            className="bg-white border px-2 py-1 rounded shadow-sm text-purple-600 cursor-pointer hover:bg-purple-50"
                                          >
                                            ID: {v.static_id || "-"}
                                          </span>
                                        ))}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-gray-500 mt-2">No visitors found.</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )) : (
                            <div className="text-center py-8 text-gray-500">No product clicks found.</div>
                          )}
                        </div>
                        <Pagination pagination={prodClicksPagination} onPageChange={(p) => setProdClicksPagination(prev => ({ ...prev, page: p }))} />
                      </>
                    )}

                    {/* Buy Clicks Tab Content */}
                    {activeTab === "buy-clicks" && (
                      <>
                        <div className="p-4 border-b flex flex-wrap gap-4 items-center bg-gray-50 rounded-t-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">From:</span>
                            <input
                              type="date"
                              className="border p-1.5 rounded text-sm"
                              value={buyClicksDraftStartDate}
                              onChange={(e) => setBuyClicksDraftStartDate(e.target.value)}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">To:</span>
                            <input
                              type="date"
                              className="border p-1.5 rounded text-sm"
                              value={buyClicksDraftEndDate}
                              onChange={(e) => setBuyClicksDraftEndDate(e.target.value)}
                            />
                          </div>
                          <button
                            className="bg-indigo-600 text-white px-4 py-1.5 rounded font-medium hover:bg-indigo-700 transition-colors text-sm"
                            onClick={() => {
                              setBuyClicksPagination(p => ({ ...p, page: 1 }));
                              setBuyClicksAppliedStartDate(buyClicksDraftStartDate);
                              setBuyClicksAppliedEndDate(buyClicksDraftEndDate);
                            }}
                          >
                            Apply
                          </button>
                          <div className="flex items-center gap-2 ml-auto">
                            <span className="text-sm font-medium text-gray-600">Sort by:</span>
                            <select
                              className="border p-1.5 rounded text-sm"
                              value={buyClicksSort}
                              onChange={(e) => { setBuyClicksPagination(p => ({ ...p, page: 1 })); setBuyClicksSort(e.target.value); }}
                            >
                              <option value="latest">Latest</option>
                              <option value="most">Most Clicks</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                          <span className="text-sm font-semibold text-gray-700">Total Buy Clicks: {buyClicksTotalEvents.toLocaleString()}</span>
                        </div>

                        <div className="w-full flex flex-col">
                          {buyClicks.length > 0 ? buyClicks.map((item, idx) => (
                            <div key={item.group_key || idx} className="border-b last:border-b-0">
                              <div
                                className="p-4 flex flex-col md:flex-row items-start md:items-center gap-4 hover:bg-gray-50 cursor-pointer"
                                onClick={() => fetchClickVisitors(item.group_key, 'checkout_redirect')}
                              >
                                <div className="w-16 h-16 bg-gray-100 rounded border overflow-hidden flex-shrink-0">
                                  {item.product_image ? (
                                    <img src={item.product_image} alt="Product" className="w-full h-full object-cover" />
                                  ) : (
                                    <ExternalLink className="w-8 h-8 m-4 text-gray-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900 truncate mb-1">
                                    {item.brand ? <span className="mr-1">{item.brand} -</span> : null}
                                    {item.product_title || 'Unknown Product'}
                                  </div>
                                  <div className="text-xs text-gray-500 mb-2">ID: {item.product_id}</div>
                                  <div className="text-xs text-gray-400">
                                    Last clicked: {item.latest_click ? formatTimestamp(item.latest_click) : '-'}
                                  </div>
                                  {item.redirect_url && (
                                    <a
                                      href={item.redirect_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-block mt-2 text-xs bg-white text-green-600 border border-green-200 px-3 py-1.5 rounded shadow-sm font-medium hover:bg-green-50 transition-colors whitespace-nowrap"
                                    >
                                      View Product
                                    </a>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1 mt-2 md:mt-0">
                                  <span className="text-sm font-medium bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full whitespace-nowrap">
                                    {item.buy_clicks} {item.buy_clicks === 1 ? 'Buy Click' : 'Buy Clicks'}
                                  </span>
                                </div>
                              </div>
                              {expandedClickItem === item.group_key && (
                                <div className="bg-gray-50 p-4 pt-0 text-sm">
                                  {loadingListVisitors ? (
                                    <div className="text-green-600 animate-pulse">Loading visitors...</div>
                                  ) : clickVisitors.length > 0 ? (
                                    <>
                                      <div className="text-gray-500 font-medium mb-1 mt-2">{clickVisitors.length} Unique Visitors</div>
                                      <div className="flex flex-wrap gap-2">
                                        {clickVisitors.map(v => (
                                          <span
                                            key={v.visitor_id}
                                            onClick={() => { setSelectedVisitor(v.visitor_id); fetchSingleVisitor(v.visitor_id); }}
                                            className="bg-white border px-2 py-1 rounded shadow-sm text-green-600 cursor-pointer hover:bg-green-50"
                                          >
                                            ID: {v.static_id || "-"}
                                          </span>
                                        ))}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-gray-500 mt-2">No visitors found.</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )) : (
                            <div className="text-center py-8 text-gray-500">No buy clicks found.</div>
                          )}
                        </div>
                        <Pagination pagination={buyClicksPagination} onPageChange={(p) => setBuyClicksPagination(prev => ({ ...prev, page: p }))} />
                      </>
                    )}

                    {/* Image Searches Tab Content */}
                    {activeTab === "image-searches" && (
                      <>
                        <div className="p-4 border-b flex flex-wrap gap-4 items-center bg-gray-50 rounded-t-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">From:</span>
                            <input
                              type="date"
                              className="border p-1.5 rounded text-sm"
                              value={imageSearchesStartDate}
                              onChange={(e) => { setImageSearchesPage(1); setImageSearchesStartDate(e.target.value); }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">To:</span>
                            <input
                              type="date"
                              className="border p-1.5 rounded text-sm"
                              value={imageSearchesEndDate}
                              onChange={(e) => { setImageSearchesPage(1); setImageSearchesEndDate(e.target.value); }}
                            />
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            <span className="text-sm font-medium text-gray-600">Sort by:</span>
                            <select
                              className="border p-1.5 rounded text-sm"
                              value={imageSearchesSort}
                              onChange={(e) => { setImageSearchesPage(1); setImageSearchesSort(e.target.value); }}
                            >
                              <option value="latest">Latest</option>
                              <option value="oldest">Oldest</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                          <span className="text-sm font-semibold text-gray-700">Total Image Searches: {imageSearchesTotalEvents.toLocaleString()}</span>
                        </div>

                        <div className="w-full">
                          {imageSearches.length > 0 ? imageSearches.map((i) => (
                            <div key={i.id} className="border-b last:border-b-0" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 120px' }}>
                              <div
                                className="p-4 flex gap-4 items-center hover:bg-gray-50"
                              >
                                <div className="w-20 h-20 bg-gray-100 rounded border overflow-hidden flex-shrink-0" style={{ willChange: 'transform' }}>
                                  {i.image_url ? (
                                    <img
                                      src={i.image_url}
                                      alt="Search"
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                      decoding="async"
                                      width={80}
                                      height={80}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm text-gray-500 mb-2">
                                    {formatTimestamp(i.createdAt)}
                                  </div>

                                  {i.buy_clicks_count > 0 && (
                                    <div className="mb-3 bg-pink-50 border border-pink-100 p-3 rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                                      <div>
                                        <div className="text-xs font-semibold text-pink-700 uppercase tracking-wide mb-1">
                                          {i.buy_clicks_count} Buy {i.buy_clicks_count === 1 ? 'Click' : 'Clicks'}
                                        </div>
                                        <div className="text-sm text-gray-800 font-medium">
                                          {i.brand ? <span className="font-bold mr-1">{i.brand}</span> : null}
                                          {i.product_title || 'Unknown Product'}
                                        </div>
                                      </div>
                                      {i.product_url && (
                                        <a
                                          href={i.product_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs bg-white text-pink-600 border border-pink-200 px-3 py-1.5 rounded shadow-sm font-medium hover:bg-pink-50 transition-colors whitespace-nowrap"
                                        >
                                          View Identified Product
                                        </a>
                                      )}
                                    </div>
                                  )}

                                  <div className="text-gray-500 font-medium mb-1 text-sm">Visitor</div>
                                  <div className="flex flex-wrap gap-2">
                                    <span
                                      onClick={() => { setSelectedVisitor(i.visitor_id); fetchSingleVisitor(i.visitor_id); }}
                                      className="bg-white border px-2 py-1 rounded shadow-sm text-fuchsia-600 cursor-pointer hover:bg-fuchsia-50 text-sm"
                                    >
                                      ID: {i.static_id || "-"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )) : (
                            <div className="text-center py-8 text-gray-500">No image searches found.</div>
                          )}
                        </div>
                        <Pagination pagination={imageSearchesPagination} onPageChange={setImageSearchesPage} />
                      </>
                    )}

                    {/* Feedback Tab Content */}
                    {activeTab === "feedback" && (
                      <>
                        <div className="p-4 border-b flex flex-wrap gap-4 items-center bg-gray-50 rounded-t-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">From:</span>
                            <input
                              type="date"
                              className="border p-1.5 rounded text-sm"
                              value={feedbackStartDate}
                              onChange={(e) => { setFeedbackPage(1); setFeedbackStartDate(e.target.value); }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">To:</span>
                            <input
                              type="date"
                              className="border p-1.5 rounded text-sm"
                              value={feedbackEndDate}
                              onChange={(e) => { setFeedbackPage(1); setFeedbackEndDate(e.target.value); }}
                            />
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            <span className="text-sm font-medium text-gray-600">Sort by:</span>
                            <select
                              className="border p-1.5 rounded text-sm"
                              value={feedbackSort}
                              onChange={(e) => { setFeedbackPage(1); setFeedbackSort(e.target.value); }}
                            >
                              <option value="latest">Latest</option>
                              <option value="oldest">Oldest</option>
                            </select>
                          </div>
                        </div>

                        <div className="w-full border-x">
                          <table className="w-full border-collapse text-sm">
                            <thead className="bg-gray-100 shadow-sm">
                              <tr>
                                <th className="px-3 py-3 text-center border-b font-semibold text-gray-700">
                                  Keyword
                                </th>
                                <th className="px-3 py-3 text-center border-b font-semibold text-gray-700">
                                  Satisfied with search results?
                                </th>
                                <th className="px-3 py-3 text-center border-b font-semibold text-gray-700">
                                  Reason
                                </th>
                                <th className="px-3 py-3 text-center border-b font-semibold text-gray-700">
                                  Date
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {allSearchFeedbacks.length > 0 ? (
                                allSearchFeedbacks.map((i, index) => (
                                  <tr
                                    key={index}
                                    className="hover:bg-gray-50 transition-colors border-b last:border-b-0"
                                  >
                                    <td className="px-3 py-3 text-center font-medium">
                                      {i?.analytics?.data?.data?.keyword || "-"}
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                      {i?.feedback === "up" ? (
                                        <span className="text-green-600 font-semibold bg-green-50 px-2 py-1 rounded">
                                          👍 Yes
                                        </span>
                                      ) : (
                                        <span className="text-red-500 font-semibold bg-red-50 px-2 py-1 rounded">
                                          👎 No
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 text-center text-gray-600">
                                      {i?.reason || "-"}
                                    </td>
                                    <td className="px-3 py-3 text-center text-gray-500">
                                      {i?.createdAt
                                        ? new Date(i.createdAt).toLocaleString()
                                        : "-"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center py-8 text-gray-500">
                                    No feedback found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <Pagination pagination={feedbackPagination} onPageChange={setFeedbackPage} />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

    </>
  );
}
